import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../shadcn/components/ui/tabs";
import { getAuctionEventsForBidder, getBidderByUserUuid } from "services";
import { useSelector } from "react-redux";
import Pusher from "pusher-js";
import PropertyAuctionCardWrapper from "./PropertyAuctionCardWrapper";
import AuctionDetailsTab from "./AuctionDetailsTab";
import CancelledEventModel from "./CancelledEventModel";

export default function AuctionEventsWrapper() {
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const [events, setEvents] = useState<any[]>([]);
  const eventsRef = useRef<any[]>(events);
  const [pastEvents, setPastEvents] = useState<any[]>([]);
  const pastEventsRef = useRef<any[]>(pastEvents);
  const [liveEvents, setLiveEvents] = useState<any[]>([]);
  const liveEventsRef = useRef<any[]>(liveEvents);
  const [cancelledEvents, setCancelledEvents] = useState<any[]>([]);
  const [bidApplicationRequests, setBidApplicationRequests] = useState<any[]>(
    [],
  );

  const init = useCallback(
    async (uuid: string) => {
      const eligibleBidder: any = await getBidderByUserUuid(uuid);
      const eventUuidsForBidder = eligibleBidder.map(
        (item: any) => item.event_uuid,
      );
      const allEventsData: any = await getAuctionEventsForBidder(
        eventUuidsForBidder,
        [
          "pending",
          "accepted",
          "live",
          "end",
          "cancelled",
          "pending_bid_approval",
          "success",
          "failed",
          "holding_bid_approval",
          "bid_rejected",
        ],
      );
      const isUserEligibleForEvent = (event: any, requireVerified = false) => {
        return event?.bidders?.some((bidder: any) => {
          const isCurrentUser = bidder?.user_uuid === onboardingData?.user_uuid;
          if (!isCurrentUser) return false;

          if (requireVerified) {
            return bidder?.status === "verified";
          }

          if (bidder?.created_by === "user") {
            return true;
          } else if (
            bidder?.created_by === "banker" &&
            bidder?.status === "verified"
          ) {
            return true;
          }
          return false;
        });
      };

      const upcomingEvents = allEventsData?.filter((event: any) => {
        const isUpcomingStatus = ["pending", "accepted"].includes(
          event?.status,
        );
        return isUpcomingStatus && isUserEligibleForEvent(event);
      });

      const pastEvents = allEventsData?.filter((event: any) => {
        const isPastStatus = [
          "end",
          "failed",
          "success",
          "pending_bid_approval",
          "holding_bid_approval",
          "bid_rejected",
        ].includes(event?.status);
        return isPastStatus && isUserEligibleForEvent(event, true);
      });

      const liveEvents = allEventsData?.filter((event: any) => {
        const isLiveStatus = event?.status === "live";
        return isLiveStatus && isUserEligibleForEvent(event, true);
      });

      const cancelledEvents = allEventsData?.filter((event: any) => {
        const isCancelledStatus = event?.status === "cancelled";
        return isCancelledStatus && isUserEligibleForEvent(event);
      });

      const applicationRequests = allEventsData?.filter((event: any) => {
        const isEventEnded = [
          "end",
          "failed",
          "success",
          "pending_bid_approval",
          "bid_rejected",
        ].includes(event?.status);
        return event?.bidders?.some((bidder: any) => {
          const isCurrentUser = bidder?.user_uuid === onboardingData?.user_uuid;
          if (!isCurrentUser) return false;
          if (isEventEnded) {
            return (
              bidder?.status !== "verified" &&
              bidder?.status !== "rejected" &&
              bidder?.created_by === "user"
            );
          }
          if (!isEventEnded) {
            return (
              bidder?.status !== "verified" && bidder?.created_by === "user"
            );
          }
          return false;
        });
      });
      setEvents(upcomingEvents);
      setPastEvents(pastEvents);
      setLiveEvents(liveEvents);
      setCancelledEvents(cancelledEvents);
      setBidApplicationRequests(applicationRequests);
    },
    [onboardingData?.user_uuid],
  );

  const user = useSelector((state: any) => {
    return state.bidderAuth.user;
  });

  useEffect(() => {
    if (user) {
      init(user.uuid);
    }
  }, [user, init]);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    liveEventsRef.current = liveEvents;
  }, [liveEvents]);

  useEffect(() => {
    pastEventsRef.current = pastEvents;
  }, [pastEvents]);

  const handleAuctionEvents = (data: any) => {
    // Validate input data
    if (!data?.event_uuid || !data?.event_type) {
      console.warn("Invalid event data received:", data);
      return;
    }

    console.log("handleAuctionEvents received data:", data);

    const eventUuid = data.event_uuid;
    const eventType = data.event_type;

    // Helper function to check if user is verified for an event
    const isUserVerifiedForEvent = (event: any) => {
      return event?.bidders?.some(
        (bidder: any) =>
          bidder?.user_uuid === onboardingData?.user_uuid &&
          bidder?.status === "verified",
      );
    };

    // Helper function to move event between categories
    const moveEventToCategory = (
      fromCategory: any[],
      toCategory: any[],
      event: any,
      categoryName: string,
    ) => {
      const isAlreadyInCategory = toCategory.some(
        (e: any) => e.uuid === event.uuid,
      );

      if (!isAlreadyInCategory) {
        console.log(`Moving event to ${categoryName}:`, event.uuid);
        return [...toCategory, event];
      }
      return toCategory;
    };

    if (eventType === "auction_event_has_started") {
      console.log("Auction started, moving from upcoming to live:", eventUuid);

      // Batch state updates for better performance
      setEvents((prevUpcoming: any[]) => {
        const startedEvent = prevUpcoming.find(
          (event: any) => event.uuid === eventUuid,
        );

        if (startedEvent) {
          // Remove from upcoming events
          const updatedUpcoming = prevUpcoming.filter(
            (event: any) => event.uuid !== eventUuid,
          );

          // Add to live events if user is verified
          if (isUserVerifiedForEvent(startedEvent)) {
            setLiveEvents((prevLive: any[]) =>
              moveEventToCategory(
                prevLive,
                prevLive,
                startedEvent,
                "live events",
              ),
            );
          }

          console.log(
            "Removed event from upcoming events, remaining:",
            updatedUpcoming.length,
          );
          return updatedUpcoming;
        }

        return prevUpcoming;
      });
    }

    if (eventType === "auction_event_has_ended") {
      console.log("Auction ended, moving to past events:", eventUuid);

      // Handle live events → past events
      setLiveEvents((prevLive: any[]) => {
        const endedEvent = prevLive.find(
          (event: any) => event.uuid === eventUuid,
        );

        if (endedEvent) {
          const updatedLive = prevLive.filter(
            (event: any) => event.uuid !== eventUuid,
          );

          // Add to past events if user was verified
          if (isUserVerifiedForEvent(endedEvent)) {
            setPastEvents((prevPast: any[]) =>
              moveEventToCategory(
                prevPast,
                prevPast,
                endedEvent,
                "past events",
              ),
            );
          }

          console.log(
            "Removed event from live events, remaining:",
            updatedLive.length,
          );
          return updatedLive;
        }

        return prevLive;
      });

      // Handle upcoming events → past events
      setEvents((prevUpcoming: any[]) => {
        const endedEvent = prevUpcoming.find(
          (event: any) => event.uuid === eventUuid,
        );

        if (endedEvent) {
          const updatedUpcoming = prevUpcoming.filter(
            (event: any) => event.uuid !== eventUuid,
          );

          // Add to past events if user was verified
          if (isUserVerifiedForEvent(endedEvent)) {
            setPastEvents((prevPast: any[]) =>
              moveEventToCategory(
                prevPast,
                prevPast,
                endedEvent,
                "past events from upcoming",
              ),
            );
          }

          console.log(
            "Removed event from upcoming events, remaining:",
            updatedUpcoming.length,
          );
          return updatedUpcoming;
        }

        return prevUpcoming;
      });
    }
  };

  useEffect(() => {
    Pusher.logToConsole = false;
    const pusher = new Pusher(`${process.env.NEXT_PUBLIC_PUSHER_KEY}`, {
      cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER,
    });

    const channelId = `update-dashboard`;
    const channel = pusher.subscribe(channelId);

    channel.bind("auction_event_has_started", (data: any) => {
      if (data) {
        console.log("data: ", data);
        if (user) {
          // init(user.uuid);
          handleAuctionEvents(data);
        }
      }
    });

    channel.bind("auction_event_has_ended", (data: any) => {
      if (data) {
        console.log("data: ", data);
        if (user) {
          // init(user.uuid);
          handleAuctionEvents(data);
        }
      }
    });

    pusher.connection.bind("error", function (err: any) {
      if (err?.error?.data?.code === 4004) {
        console.log(">>> detected limit error");
      } else if (err?.data?.code === 1006) {
        // Websocket: CLOSED ABNORMALLY
      }
    });

    pusher.connection.bind("disconnected", function (err: any) {
      console.log(">>> disconnected");
    });

    pusher.connection.bind("connected", function (err: any) {
      console.log("connected", pusher.sessionID);
    });

    return function cleanup() {
      pusher.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, init]);

  const tabMapping = useMemo(
    () => [
      {
        name: "Upcoming Events",
        key: "upcoming_events",
        data: events,
      },
      {
        name: "Live Events",
        key: "live_events",
        data: liveEvents,
      },
      {
        name: "Past Events",
        key: "past_events",
        data: pastEvents,
      },
      {
        name: "Cancelled Events",
        key: "cancelled_events",
        data: cancelledEvents,
      },
      {
        name: "Bid Applications",
        key: "bid_application",
        data: bidApplicationRequests,
      },
    ],
    [events, liveEvents, pastEvents, cancelledEvents, bidApplicationRequests],
  );
  const [openDetailTab, setOpenDetailsTab] = useState(null);
  const [defaultActiveTabValue, setDefaultActiveTabValue] =
    useState("upcoming_events");

  const [openCancelledEventModal, setOpenCancelledEventModal] =
    useState<any>(false);

  return (
    <div className="max-w-7xl">
      <div>
        {!openDetailTab && (
          <Tabs defaultValue={defaultActiveTabValue} className="w-full">
            <TabsList className="w-full lg:gap-[50px] font-openSans font-normal leading-4 text-[#A8A8A8] bg-transparent">
              {tabMapping.map((item, index) => (
                <TabsTrigger
                  value={item.key}
                  className="uppercase px-2 mobile:px-[6px] sm:px-2 font-normal lg:font-semibold font-sans data-[state=active]:bg-transparent text-[6px] mobile:text-[7px] sm:text-[8px] lg:text-sm"
                  key={`AuctionEvents_${item.name}`}
                >
                  {item.name}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabMapping.map((item, index) => (
              <TabsContent
                value={item.key}
                className="mt-4 md:mt-7"
                key={`Auction_Event_Card_tab_trigger_${item.name}_${index}`}
              >
                <PropertyAuctionCardWrapper
                  activeTabData={item}
                  setOpenDetailsTab={setOpenDetailsTab}
                  setOpenCancelledEventModal={setOpenCancelledEventModal}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
        {openDetailTab && (
          <AuctionDetailsTab
            eventData={openDetailTab}
            setOpenDetailsTab={setOpenDetailsTab}
            setDefaultActiveTabValue={setDefaultActiveTabValue}
          />
        )}
        {openCancelledEventModal && (
          <CancelledEventModel
            openCancelledEventModal={openCancelledEventModal}
            setOpenCancelledEventModal={setOpenCancelledEventModal}
          />
        )}
      </div>
    </div>
  );
}
